import {
  Alert,
  Button,
  Columns,
  FormField,
  Input,
  Section,
  SegmentedControl,
  Stack,
  useToast,
} from '@ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formErrors } from '../../../lib/errors';
import { setTheme, useThemePreference, type ThemePreference } from '../../../lib/theme';
import { useChangePassword, useUpdateProfile } from '../api';
import { PasswordInput } from '../components/PasswordInput';
import { useSession } from '../session';

/** The signed-in person: name, appearance and password. */
export function ProfilePage() {
  return (
    <Columns>
      <ProfileForm />
      <Appearance />
      <PasswordForm />
    </Columns>
  );
}

function ProfileForm() {
  const { t } = useTranslation();
  const toast = useToast();
  const { me } = useSession();
  const update = useUpdateProfile();
  // Accounts from before #16 may lack the first name; the field is then empty and required.
  const [firstName, setFirstName] = useState(me.first_name);
  const [lastName, setLastName] = useState(me.last_name);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate(
      { first_name: firstName, last_name: lastName, locale: me.locale },
      { onSuccess: () => toast.show(t('common.saved')) },
    );
  };
  const errors = formErrors(update.error, {
    firstName: ['first_name.'],
    lastName: ['last_name.'],
  });

  return (
    <Stack as="form" gap={4} onSubmit={onSubmit} noValidate>
      <Section variant="content" title={t('profile.title')}>
        {errors.general && <Alert tone="danger">{errors.general}</Alert>}
        <FormField label={t('profile.firstName')} error={errors.field.firstName} required>
          {(p) => (
            <Input
              {...p}
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          )}
        </FormField>
        <FormField label={t('profile.lastName')} error={errors.field.lastName} required>
          {(p) => (
            <Input
              {...p}
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          )}
        </FormField>
        <FormField label={t('profile.email')}>
          {(p) => <Input {...p} type="email" value={me.email} readOnly disabled />}
        </FormField>
      </Section>
      <div>
        <Button type="submit" loading={update.isPending}>
          {t('common.save')}
        </Button>
      </div>
    </Stack>
  );
}

/** Light, dark or following the system; takes effect at once (docs/ux-richtlinien.md §6.15). */
function Appearance() {
  const { t } = useTranslation();
  const preference = useThemePreference();
  return (
    <Section variant="content" title={t('theme.label')}>
      <SegmentedControl<ThemePreference>
        label={t('theme.label')}
        fullWidth
        value={preference}
        onChange={setTheme}
        options={[
          { value: 'light', label: t('theme.light') },
          { value: 'dark', label: t('theme.dark') },
          { value: 'system', label: t('theme.system') },
        ]}
      />
    </Section>
  );
}

function PasswordForm() {
  const { t } = useTranslation();
  const toast = useToast();
  const change = useChangePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (next !== repeat) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    change.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: () => {
          toast.show(t('profile.passwordChanged'));
          setCurrent('');
          setNext('');
          setRepeat('');
        },
      },
    );
  };
  const errors = formErrors(change.error, {
    current: ['password.current_wrong'],
    next: ['password.'],
  });

  return (
    <Stack as="form" gap={4} onSubmit={onSubmit} noValidate>
      <Section variant="content" title={t('profile.passwordTitle')}>
        {errors.general && <Alert tone="danger">{errors.general}</Alert>}
        <FormField label={t('profile.currentPassword')} error={errors.field.current} required>
          {(p) => (
            <PasswordInput {...p} value={current} onChange={(e) => setCurrent(e.target.value)} />
          )}
        </FormField>
        <FormField
          label={t('profile.newPassword')}
          hint={t('auth.passwordHint')}
          error={errors.field.next}
          required
        >
          {(p) => (
            <PasswordInput
              {...p}
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          )}
        </FormField>
        <FormField
          label={t('profile.repeatPassword')}
          error={mismatch ? t('auth.reset.mismatch') : undefined}
          required
        >
          {(p) => (
            <PasswordInput
              {...p}
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          )}
        </FormField>
      </Section>
      <div>
        <Button type="submit" variant="secondary" loading={change.isPending}>
          {t('profile.changePassword')}
        </Button>
      </div>
    </Stack>
  );
}
